import { Eraser, Paintbrush, Trash2, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type Tool = 'brush' | 'eraser';

type MagicEditToolbarProps = {
  tool: Tool;
  onToolChange: (tool: Tool) => void;
  brushSize: number;
  onBrushSizeChange: (value: number) => void;
  canUndo: boolean;
  onUndo: () => void;
  onClear: () => void;
  className?: string;
  isMobileView?: boolean;
};

export const magicEditToolbarSelectedButtonClass =
  'cursor-default hover:!border-transparent hover:!bg-primary-light hover:!text-white hover:!opacity-100 active:!border-transparent active:!bg-primary-light active:!text-white active:!opacity-100';

export const magicEditToolbarToggleButtonClass = (isSelected: boolean, isMobileView?: boolean) =>
  cn(
    'h-7 w-7 rounded-sm p-0',
    isSelected
      ? cn('bg-primary-light text-white', magicEditToolbarSelectedButtonClass)
      : cn(
          'text-slate-500',
          isMobileView &&
            'hover:!border-transparent hover:!bg-transparent active:!border-transparent active:!bg-transparent'
        )
  );

export const magicEditToolbarActionButtonClass = (isMobileView?: boolean) =>
  cn(
    'h-7 w-7 rounded-sm p-0 text-slate-500',
    isMobileView &&
      'hover:!border-transparent hover:!bg-transparent active:!border-transparent active:!bg-transparent'
  );

export const MagicEditToolbar = ({
  tool,
  onToolChange,
  brushSize,
  onBrushSizeChange,
  canUndo,
  onUndo,
  onClear,
  className,
  isMobileView,
}: MagicEditToolbarProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-sm border border-slate-200 bg-white p-1 shadow-sm',
        className
      )}
    >
      <Button
        type="button"
        variant="ghost"
        className={magicEditToolbarToggleButtonClass(tool === 'brush', isMobileView)}
        onClick={() => onToolChange('brush')}
        aria-label={t('brush')}
      >
        <Paintbrush aria-hidden className="size-3" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        className={magicEditToolbarToggleButtonClass(tool === 'eraser', isMobileView)}
        onClick={() => onToolChange('eraser')}
        aria-label={t('eraser')}
      >
        <Eraser aria-hidden className="size-3" />
      </Button>

      <div className="mx-0.5 h-4 w-px bg-slate-200" />

      <div className="flex items-center gap-1.5">
        <Input
          value={String(brushSize)}
          onChange={(e) => {
            if (e.target.value === '') return;
            const next = Number(e.target.value);
            if (Number.isFinite(next)) onBrushSizeChange(next);
          }}
          className="h-7 w-12 rounded-sm px-1.5 text-xs"
          inputMode="numeric"
        />
        <span className="text-xs text-slate-500">px</span>
      </div>

      <div className="mx-0.5 h-4 w-px bg-slate-200" />

      <Button
        type="button"
        variant="ghost"
        className={magicEditToolbarActionButtonClass(isMobileView)}
        onClick={onUndo}
        disabled={!canUndo}
        aria-label={t('undo')}
      >
        <Undo2 aria-hidden className="size-3" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        className={magicEditToolbarActionButtonClass(isMobileView)}
        onClick={onClear}
        aria-label={t('clear')}
      >
        <Trash2 aria-hidden className="size-3" />
      </Button>
    </div>
  );
};
